// src/components/Eventos/EventsHeats/hooks/useEventHeatsData.js
import { useMemo } from 'react';
import { useEventContext, fieldHorizontalEvents, fieldVerticalEvents } from '../../../../context/EventContext';
import computeLapHistory from '../../../../utils/computeLapHistory';
import { safe, isSpecial, hasValidResult, sortByPlace } from '../utils/eventHelpers';
import { computeFieldPositions, getAthletePositionStats, formatAttemptsWithPositions } from '../utils/fieldPositionTracker';
import { 
  computeVerticalPositions, 
  getAthleteVerticalPositionStats, 
  formatHeightsWithPositions,
  filterHeightsWithAttempts,
  filterHeightsForAthlete
} from '../utils/verticalPositionTracker';
import { validateVerticalData, debugAthleteHeightFiltering } from '../utils/verticalDebugHelper';

export const useEventHeatsData = (eventNumber, round) => {
  const {
    evtData, 
    getParticipantResults, 
    participantsResults,
    isTrackEventFn, 
    precision: globalPrecision, 
    getTestSettings,
    getPlace,
    hasPassed,
    heatHeights
  } = useEventContext();

  // Filtrar heats de este evento/ronda
  const eventDetails = useMemo(
    () => evtData.filter(ev =>
      +ev.eventNumber === +eventNumber &&
      +ev.round === +round),
    [evtData, eventNumber, round]
  );

  // Información básica del evento
  const genderLabel = useMemo(() => {
    if (!eventDetails.length) return 'Mixto';
    const n = eventDetails[0].eventName.toLowerCase();
    if (n.includes('varonil') || n.includes('men')) return 'Varonil';
    if (n.includes('femenil') || n.includes('women')) return 'Femenil';
    if (n.includes('girls')) return 'Niñas';
    if (n.includes('boys')) return 'Niños';
    return 'Mixto';
  }, [eventDetails]);

  const totalParts = useMemo(() => {
    if (!eventDetails.length) return 0;
    const cnt = ps => safe(ps).filter(p => p.idNumber?.trim()).length;
    if (eventDetails[0].isCombined) return cnt(eventDetails[0].participants);
    return eventDetails.reduce((a, ev) => a + cnt(ev.participants), 0);
  }, [eventDetails]);

  const isFieldEvent = useMemo(() => {
    if (!eventDetails.length) return false;
    const code = eventDetails[0].eventCode;
    return fieldHorizontalEvents.includes(code)
        || fieldVerticalEvents.includes(code);
  }, [eventDetails]);
  
  const isVerticalEvent = useMemo(() => {
    if (!eventDetails.length) return false;
    const code = eventDetails[0].eventCode;
    return fieldVerticalEvents.includes(code);
  }, [eventDetails]);
  
  const hasPlaceOrResult = useMemo(() => {
    // Verificar si hay resultados reales en cualquier evento (pista O campo)
    return eventDetails.some(ev => {
      const k = `${ev.eventNumber}_${ev.round}_${ev.heat}`;
      
      // Verificar si hay lugares asignados
      if (
        Object.values(participantsResults[k] || {})
          .some(pr => pr.place && pr.place !== '-')
      ) return true;
      
      // Verificar si hay resultados para cada participante
      return ev.participants.some(p => {
        const pr = getParticipantResults(ev.eventNumber, ev.round, ev.heat, p.idNumber) || {};
        
        // Para eventos de pista: verificar tiempo o marca especial
        if (pr.time || pr.special) return true;
        
        // Para eventos de campo horizontales: verificar si hay intentos con resultados válidos
        if (Array.isArray(pr.attempts) && pr.attempts.length > 0) {
          return pr.attempts.some(attempt => 
            attempt && 
            attempt.result && 
            attempt.result !== '-' && 
            attempt.result !== ''
          );
        }
        
        // Para eventos verticales: verificar si hay intentos en las alturas
        if (pr.heights && typeof pr.heights === 'object') {
          return Object.values(pr.heights).some(heightData => 
            heightData.attempts && 
            heightData.attempts.some(attempt => 
              attempt && attempt !== '' && attempt !== '-'
            )
          );
        }
        
        return false;
      });
    });
  }, [eventDetails, participantsResults, getParticipantResults]);

  // Equipos únicos para filtrado
  const uniqueTeams = useMemo(() => {
    const teams = new Set();
    eventDetails.forEach(ev => {
      safe(ev.participants).forEach(p => {
        if (p.affiliation) teams.add(p.affiliation);
      });
    });
    return [...teams].sort();
  }, [eventDetails]);

  // Helper para calcular la mejor altura alcanzada en eventos verticales
  const getBestHeightForVertical = (participantResults, heights) => {
    if (!participantResults.heights || !heights) return null;
    
    const clearedHeights = heights
      .filter(height => {
        const heightKey = height.value.toFixed(2);
        const heightResult = participantResults.heights[heightKey];
        return heightResult && heightResult.attempts && heightResult.attempts.includes('O');
      })
      .map(height => height.value);
    
    return clearedHeights.length > 0 ? Math.max(...clearedHeights) : null;
  };

  // Helper para procesar datos de eventos verticales
  const processVerticalAttempts = (participantResults, heights, positionStats) => {
    if (!participantResults.heights || !heights) return [];
    
    return formatHeightsWithPositions(heights, participantResults, positionStats);
  };

  // Función para procesar las filas de un heat
  const processHeatRows = (heat) => {
    const key = `${heat.eventNumber}_${heat.round}_${heat.heat}`;
    const isTrack = isTrackEventFn(heat.eventCode);
    const isVertical = fieldVerticalEvents.includes(heat.eventCode);
    const isHorizontal = fieldHorizontalEvents.includes(heat.eventCode);
    const { enableWind } = getTestSettings(heat.eventNumber, heat.round);

    // Obtener alturas para eventos verticales
    const allHeights = isVertical ? (heatHeights[key] || []) : [];
    
    // Filtrar alturas que tienen al menos un intento registrado
    const athletesData = safe(heat.participants).map(p => ({
      idNumber: p.idNumber,
      results: getParticipantResults(heat.eventNumber, heat.round, heat.heat, p.idNumber) || {}
    }));
    
    const heights = isVertical ? (() => {
      // Debug para desarrollo (remover en producción)
      if (process.env.NODE_ENV === 'development' && allHeights.length > 0) {
        validateVerticalData(athletesData, allHeights);
        // El debug individual se hace por atleta en el procesamiento de filas
      }
      
      return filterHeightsWithAttempts(athletesData, allHeights);
    })() : [];

    let maxLaps = 0;
    let maxAttempts = 0;
    let hasScores = false;
    let hasYear = false;

    const rows = safe(heat.participants).map(p => {
      const pr = getParticipantResults(heat.eventNumber, heat.round, heat.heat, p.idNumber) ?? {};
      const { 
        lapTimes = [], attempts = [], timeRaw, wind, special, time,
        precision: prec, reactionTime, PB, SB, MR, qualifier, score,
        heights: verticalHeights
      } = pr;

      if (score != null) hasScores = true;
      if (p.year) hasYear = true;
      if (lapTimes.length) maxLaps = Math.max(maxLaps, lapTimes.length);
      
      // Para eventos horizontales: calcular maxAttempts
      if (isHorizontal && attempts && attempts.length > 0) {
        const validAttempts = attempts.filter(attempt => 
          attempt && 
          attempt.result !== undefined && 
          attempt.result !== null && 
          attempt.result !== ''
        );
        
        if (validAttempts.length > 0) {
          maxAttempts = Math.max(maxAttempts, validAttempts.length);
        }
      }

      const chips = [];
      if (PB) chips.push('PB');
      if (SB) chips.push('SB');
      if (MR) chips.push('MR');
      if (qualifier === 'Q' || qualifier === 'q') chips.push(qualifier);

      let best = null;
      if (isHorizontal && attempts.length) {
        const ok = attempts.map((a, i) => ({ ...a, i }))
          .filter(a => !isSpecial(a.result) && +a.result > 0)
          .sort((a, b) => +b.result - +a.result);
        if (ok.length) best = ok[0].i;
      }

      const place = getPlace(heat.eventNumber, heat.round, heat.heat, p.idNumber);
      const passed = hasPassed(heat.eventNumber, heat.round, heat.heat, p.idNumber);
      const full = p.fullName || `${p.firstName || ''} ${p.lastName || ''}`.trim();

      const rowObj = {
        place, 
        passed,
        line: (p.lane === 0 || p.lane === '0') ? '-' : (p.lane || '-'),
        number: p.idNumber, 
        name: full, 
        team: p.affiliation || '',
        result: (() => {
          // Para eventos verticales, mostrar la mejor altura alcanzada
          if (isVertical) {
            const bestHeight = getBestHeightForVertical(pr, heights);
            if (bestHeight !== null) {
              return `${bestHeight.toFixed(2)} m`;
            }
            // Si no hay altura válida, verificar estado especial
            return pr.specialStatus && pr.specialStatus !== 'Valid' ? pr.specialStatus : 'NM';
          }
          
          // Para eventos horizontales, mostrar la mejor marca de los intentos
          if (isHorizontal && attempts.length > 0) {
            const validAttempts = attempts.filter(a => {
              return a && 
                a.result && 
                a.result !== '-' && 
                a.result !== '' &&
                !isSpecial(a.result) && 
                parseFloat(a.result) > 0;
            });
            
            if (validAttempts.length > 0) {
              const bestAttempt = validAttempts.reduce((best, current) => {
                const currentValue = parseFloat(current.result);
                const bestValue = parseFloat(best.result);
                return currentValue > bestValue ? current : best;
              });
              
              return bestAttempt.result;
            }
          }
          
          // Para eventos de pista o si no hay intentos válidos
          return special || time || '-';
        })(),
        attemptsData: attempts, 
        bestAttemptIndex: best,
        isTrackEvent: isTrack, 
        enableWind, 
        wind, 
        lapTimes,
        reactionTime, 
        timeRaw, 
        athPrec: prec || globalPrecision,
        chips, 
        score, 
        year: p.year || null,
        athletePhoto: null, 
        teamLogo: null, 
        heatNum: heat.heat,
        eventCode: heat.eventCode,
        // Datos específicos para eventos verticales (se actualizarán en el procesamiento)
        heightsData: null,
        verticalAttempts: null
      };

      return rowObj;
    });

    // Calcular historial de vueltas para eventos de pista
    const lapHist = computeLapHistory(
      rows.map(r => ({ number: r.number, lapTimes: r.lapTimes })),
      maxLaps
    );

    // Calcular posiciones de intentos para eventos horizontales
    let fieldPositions = {};
    if (isHorizontal && maxAttempts > 0) {
      const athletesWithAttempts = rows.map(r => ({
        number: r.number,
        attemptsData: r.attemptsData
      }));
      
      fieldPositions = computeFieldPositions(athletesWithAttempts, maxAttempts);
    }

    // Calcular posiciones para eventos verticales
    let verticalPositions = {};
    if (isVertical && heights.length > 0) {
      const athletesWithResults = rows.map(r => ({
        idNumber: r.number,
        results: getParticipantResults(heat.eventNumber, heat.round, heat.heat, r.number) || {}
      }));
      
      verticalPositions = computeVerticalPositions(athletesWithResults, heights);
    }

    // Ordenar si hay lugares
    if (sortByPlace(rows)) {
      rows.sort((a, b) => {
        const pa = isNaN(+a.place) ? Infinity : +a.place;
        const pb = isNaN(+b.place) ? Infinity : +b.place;
        return pa - pb;
      });
    }

    // Añadir historial y posiciones a las filas
    const processedRows = rows.map(r => {
      const rowData = {
        ...r,
        hist: lapHist[String(r.number)] || {},
        totalLaps: maxLaps
      };

      // Para eventos horizontales, agregar datos de posiciones
      if (isHorizontal && maxAttempts > 0) {
        const positionStats = getAthletePositionStats(fieldPositions, r.number);
        rowData.fieldPositions = positionStats;
        rowData.attemptsWithPositions = formatAttemptsWithPositions(r.attemptsData, positionStats);
        rowData.maxAttempts = maxAttempts;
      }

      // Para eventos verticales, agregar datos de posiciones por atleta individual
      if (isVertical && allHeights.length > 0) {
        const participantResults = getParticipantResults(heat.eventNumber, heat.round, heat.heat, r.number) || {};
        const positionStats = getAthleteVerticalPositionStats(verticalPositions, r.number);
        
        // Debug individual por atleta (solo en desarrollo)
        if (process.env.NODE_ENV === 'development') {
          debugAthleteHeightFiltering(participantResults, allHeights, r.number);
        }
        
        // Procesar intentos usando TODAS las alturas (no filtradas) para calcular posiciones correctas
        rowData.verticalAttempts = processVerticalAttempts(participantResults, allHeights, positionStats);
        
        // Solo incluir las alturas que este atleta específico tiene
        rowData.heightsData = filterHeightsForAthlete(participantResults, allHeights);
      }

      return rowData;
    });

    return {
      rows: processedRows,
      hasScores,
      hasYear,
      maxLaps,
      maxAttempts,
      key
    };
  };

  // Procesar resultados globales
  const globalResults = useMemo(() => {
    const globalRows = [];
    
    eventDetails.forEach(heat => {
      const { rows } = processHeatRows(heat);
      globalRows.push(...rows);
    });

    // Filtrar solo atletas con resultados válidos
    const globalRowsWithResults = globalRows.filter(hasValidResult);

    // Ordenar por resultado dependiendo del tipo de evento
    if (isVerticalEvent) {
      // Para eventos verticales, ordenar por altura (mayor es mejor)
      globalRowsWithResults.sort((a, b) => {
        const heightA = parseFloat(a.result);
        const heightB = parseFloat(b.result);
        
        if (isNaN(heightA) && isNaN(heightB)) return 0;
        if (isNaN(heightA)) return 1;
        if (isNaN(heightB)) return -1;
        
        return heightB - heightA; // Mayor altura primero
      });
    } else {
      // Para eventos de pista y horizontales, ordenar por tiempo/distancia
      globalRowsWithResults.sort((a, b) => {
        const ta = isSpecial(a.result) ? Infinity : +a.timeRaw || Infinity;
        const tb = isSpecial(b.result) ? Infinity : +b.timeRaw || Infinity;
        return ta - tb;
      });
    }

    // Asignar lugares
    let currentPlace = 1;
    globalRowsWithResults.forEach((r, index) => {
      if (isSpecial(r.result) || (isVerticalEvent && isNaN(parseFloat(r.result)))) {
        r.place = '-';
      } else {
        r.place = currentPlace++;
      }
    });

    const globalHasYear = globalRowsWithResults.some(r => r.year != null);
    const globalHasScores = globalRowsWithResults.some(r => r.score != null);

    return {
      rows: globalRowsWithResults,
      hasYear: globalHasYear,
      hasScores: globalHasScores,
      hasAnyResults: globalRowsWithResults.length > 0
    };
  }, [eventDetails, getParticipantResults, globalPrecision, isVerticalEvent]);

  return {
    eventDetails,
    genderLabel,
    totalParts,
    isFieldEvent,
    isVerticalEvent,
    hasPlaceOrResult,
    uniqueTeams,
    processHeatRows,
    globalResults
  };
};